import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminSiteSettingsService } from '../../../services/adminSiteSettingsService';
import { Save, Plus, Trash2, HelpCircle } from 'lucide-react';
import { Button, Input, TextArea } from '../../shared';
import { useToastContext } from '../../../contexts/ToastContext';

const DEFAULT_TABS = [
  { id: 'getting-started', label: 'Getting Started', icon: 'BookOpen' },
  { id: 'bookings', label: 'Managing Bookings', icon: 'Calendar' },
  { id: 'payments', label: 'Payments & Earnings', icon: 'CreditCard' },
  { id: 'profile', label: 'Profile & Services', icon: 'Settings' },
  { id: 'resources', label: 'Resources', icon: 'FileText' },
];

const DEFAULT_FAQS = {
  'getting-started': [
    { question: 'How do I create a vendor account?', answer: 'Click on "Become a Vendor" in the navigation menu, fill out the registration form with your business details, and verify your email.' },
    { question: 'What information do I need to provide?', answer: "You'll need business information (name, description, contact details), service categories, pricing, availability, and verification documents." },
    { question: 'How long does verification take?', answer: 'Verification typically takes 1-3 business days.' },
    { question: 'What are the platform fees?', answer: 'VendaList charges a commission fee on completed bookings. The exact percentage varies by subscription plan.' },
  ],
  bookings: [
    { question: 'How do I receive booking requests?', answer: "When clients are interested, they'll send booking requests through the platform. You'll receive notifications via email and in your dashboard." },
    { question: 'How do I accept or decline bookings?', answer: 'Go to your "Bookings" page, review the request details, and click "Accept" or "Decline".' },
    { question: 'Can I modify a booking after accepting?', answer: 'Yes, you can modify booking details up to 48 hours before the event.' },
    { question: 'What happens if I need to cancel a booking?', answer: 'Cancellations should be made as early as possible. Go to the booking details and select "Cancel Booking".' },
  ],
  payments: [
    { question: 'How do I get paid?', answer: 'Payments are processed automatically after service completion. Funds are transferred within 5-7 business days.' },
    { question: 'What payment methods are supported?', answer: 'We support bank transfers, PayPal, and other payment processors.' },
    { question: 'How do I view my earnings?', answer: 'Go to your "Earnings" or "Payments" section in the vendor dashboard.' },
    { question: 'Are there any payment fees?', answer: 'Payment processing fees may apply depending on your payment method.' },
  ],
  profile: [
    { question: 'How do I update my vendor profile?', answer: 'Go to "Vendor Profile" in your dashboard, click "Edit Profile", and update your information.' },
    { question: 'How do I add or edit services?', answer: 'Navigate to "Services" in your dashboard, click "Add Service" or edit an existing service.' },
    { question: 'How do I manage my portfolio?', answer: 'Go to "Portfolio" in your dashboard to upload photos and videos of your work.' },
    { question: 'Can I set my own availability?', answer: 'Yes, go to "Availability" in your dashboard to set your working hours and blackout dates.' },
  ],
  resources: [
    { question: 'Where can I find marketing resources?', answer: 'Visit the "Resources" section in your vendor dashboard for marketing templates and tips.' },
    { question: 'Are there training materials available?', answer: 'Yes, we provide video tutorials, webinars, and written guides.' },
    { question: 'How do I access vendor analytics?', answer: 'Go to "Analytics" in your dashboard to view booking trends and performance metrics.' },
    { question: 'Is there a vendor community?', answer: 'Yes! Join our vendor community forum to connect with other vendors.' },
  ],
};

const DEFAULT_CONTACT = [
  { icon: 'MessageSquare', title: 'Live Chat', description: 'Chat with our vendor support team', action: 'Start Chat', available: true },
  { icon: 'Mail', title: 'Email Support', description: 'vendors@vendalist.com', action: 'Send Email', available: true },
  { icon: 'Phone', title: 'Phone Support', description: '1-800-VENDA-VENDOR', action: 'Call Now', available: true },
];

const DEFAULT_QUICK_LINKS = [
  { icon: 'Settings', title: 'Vendor Dashboard', description: 'Manage your account and services', href: '/vendor/dashboard' },
  { icon: 'Calendar', title: 'Booking Calendar', description: 'View and manage your bookings', href: '/vendor/calendar' },
  { icon: 'CreditCard', title: 'Earnings & Payments', description: 'Track your revenue and payments', href: '/vendor/earnings' },
  { icon: 'Users', title: 'Client Messages', description: 'Communicate with your clients', href: '/vendor/messages' },
];

const VendorSupportSettings = () => {
  const queryClient = useQueryClient();
  const toast = useToastContext();
  const [formData, setFormData] = useState({
    hero: { title: 'Vendor Support', subtitle: 'Everything you need to succeed as a vendor on VendaList' },
    tabs: DEFAULT_TABS,
    faqs: DEFAULT_FAQS,
    contact_options: DEFAULT_CONTACT,
    quick_links: DEFAULT_QUICK_LINKS,
  });

  const { data: settingsResponse, isLoading } = useQuery({
    queryKey: ['adminSiteSettings'],
    queryFn: () => adminSiteSettingsService.get(),
  });

  useEffect(() => {
    const vs = settingsResponse?.data?.vendor_support_settings;
    if (vs && typeof vs === 'object') {
      setFormData({
        hero: vs.hero || { title: 'Vendor Support', subtitle: 'Everything you need to succeed as a vendor on VendaList' },
        tabs: Array.isArray(vs.tabs) && vs.tabs.length > 0 ? vs.tabs : DEFAULT_TABS,
        faqs: vs.faqs && typeof vs.faqs === 'object' ? vs.faqs : DEFAULT_FAQS,
        contact_options: Array.isArray(vs.contact_options) ? vs.contact_options : DEFAULT_CONTACT,
        quick_links: Array.isArray(vs.quick_links) ? vs.quick_links : DEFAULT_QUICK_LINKS,
      });
    }
  }, [settingsResponse]);

  const saveMutation = useMutation({
    mutationFn: (data) => adminSiteSettingsService.save(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminSiteSettings']);
      queryClient.invalidateQueries(['siteSettings']);
      toast.success(null, 'Vendor support settings saved successfully');
    },
    onError: (error) => {
      toast.error(null, error?.response?.data?.message || 'Failed to save');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const fullSettings = { ...settingsResponse?.data, vendor_support_settings: formData };
    saveMutation.mutate(fullSettings);
  };

  const updateHero = (field, value) =>
    setFormData((prev) => ({ ...prev, hero: { ...prev.hero, [field]: value } }));

  const updateTab = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.tabs || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, tabs: arr };
    });
  };
  const addTab = () =>
    setFormData((prev) => ({
      ...prev,
      tabs: [...(prev.tabs || []), { id: 'new-tab', label: 'New Tab', icon: 'FileText' }],
    }));
  const removeTab = (i) =>
    setFormData((prev) => ({ ...prev, tabs: prev.tabs.filter((_, idx) => idx !== i) }));

  const updateFaq = (tabId, index, field, value) => {
    setFormData((prev) => {
      const faqs = { ...prev.faqs };
      if (!faqs[tabId]) faqs[tabId] = [];
      const arr = [...faqs[tabId]];
      arr[index] = { ...arr[index], [field]: value };
      faqs[tabId] = arr;
      return { ...prev, faqs };
    });
  };
  const addFaq = (tabId) => {
    setFormData((prev) => {
      const faqs = { ...prev.faqs };
      faqs[tabId] = [...(faqs[tabId] || []), { question: '', answer: '' }];
      return { ...prev, faqs };
    });
  };
  const removeFaq = (tabId, i) => {
    setFormData((prev) => {
      const faqs = { ...prev.faqs };
      faqs[tabId] = (faqs[tabId] || []).filter((_, idx) => idx !== i);
      return { ...prev, faqs };
    });
  };

  const updateContact = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.contact_options || [])];
      arr[index] = { ...arr[index], [field]: value };
      if (field === 'available') arr[index][field] = value === true || value === 'true';
      return { ...prev, contact_options: arr };
    });
  };
  const addContact = () =>
    setFormData((prev) => ({
      ...prev,
      contact_options: [...(prev.contact_options || []), { icon: 'Mail', title: '', description: '', action: '', available: true }],
    }));
  const removeContact = (i) =>
    setFormData((prev) => ({ ...prev, contact_options: prev.contact_options.filter((_, idx) => idx !== i) }));

  const updateQuickLink = (index, field, value) => {
    setFormData((prev) => {
      const arr = [...(prev.quick_links || [])];
      arr[index] = { ...arr[index], [field]: value };
      return { ...prev, quick_links: arr };
    });
  };
  const addQuickLink = () =>
    setFormData((prev) => ({
      ...prev,
      quick_links: [...(prev.quick_links || []), { icon: 'Settings', title: '', description: '', href: '' }],
    }));
  const removeQuickLink = (i) =>
    setFormData((prev) => ({ ...prev, quick_links: prev.quick_links.filter((_, idx) => idx !== i) }));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow">
      <form onSubmit={handleSubmit} className="p-6 space-y-8">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Vendor Support Page
          </h2>
          <p className="text-sm text-gray-600">Configure the content for the vendor support page at /vendor/support</p>
        </div>

        {/* Hero */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Hero Section</h3>
          <div className="space-y-4">
            <Input label="Title" value={formData.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} placeholder="Vendor Support" />
            <TextArea label="Subtitle" value={formData.hero?.subtitle || ''} onChange={(e) => updateHero('subtitle', e.target.value)} rows={2} placeholder="Everything you need to succeed..." />
          </div>
        </div>

        {/* Tabs */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">FAQ Tabs</h3>
            <Button type="button" variant="outline" size="sm" onClick={addTab}><Plus className="w-4 h-4 mr-1" /> Add Tab</Button>
          </div>
          <div className="space-y-4">
            {(formData.tabs || []).map((tab, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Tab {i + 1}</span>
                  <button type="button" onClick={() => removeTab(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Input label="ID (e.g. getting-started)" value={tab.id || ''} onChange={(e) => updateTab(i, 'id', e.target.value)} />
                  <Input label="Label" value={tab.label || ''} onChange={(e) => updateTab(i, 'label', e.target.value)} />
                  <Input label="Icon (e.g. BookOpen)" value={tab.icon || ''} onChange={(e) => updateTab(i, 'icon', e.target.value)} placeholder="BookOpen" />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* FAQs by Tab */}
        <div className="border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">FAQs by Tab</h3>
          {(formData.tabs || []).map((tab) => (
            <div key={tab.id} className="mb-6 last:mb-0">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-gray-800">{tab.label}</h4>
                <Button type="button" variant="outline" size="sm" onClick={() => addFaq(tab.id)}><Plus className="w-4 h-4 mr-1" /> Add FAQ</Button>
              </div>
              <div className="space-y-3">
                {(formData.faqs?.[tab.id] || []).map((faq, j) => (
                  <div key={j} className="p-3 bg-surface-muted rounded-lg space-y-2">
                    <div className="flex justify-end">
                      <button type="button" onClick={() => removeFaq(tab.id, j)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <Input label="Question" value={faq.question || ''} onChange={(e) => updateFaq(tab.id, j, 'question', e.target.value)} />
                    <TextArea label="Answer" value={faq.answer || ''} onChange={(e) => updateFaq(tab.id, j, 'answer', e.target.value)} rows={2} />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Contact Options */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Contact Options</h3>
            <Button type="button" variant="outline" size="sm" onClick={addContact}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.contact_options || []).map((opt, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Option {i + 1}</span>
                  <button type="button" onClick={() => removeContact(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Icon (MessageSquare, Mail, Phone)" value={opt.icon || ''} onChange={(e) => updateContact(i, 'icon', e.target.value)} />
                  <Input label="Title" value={opt.title || ''} onChange={(e) => updateContact(i, 'title', e.target.value)} />
                  <Input label="Description" value={opt.description || ''} onChange={(e) => updateContact(i, 'description', e.target.value)} />
                  <Input label="Button Text" value={opt.action || ''} onChange={(e) => updateContact(i, 'action', e.target.value)} />
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={opt.available !== false} onChange={(e) => updateContact(i, 'available', e.target.checked)} />
                    <span className="text-sm">Available</span>
                  </label>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium text-gray-900">Quick Links</h3>
            <Button type="button" variant="outline" size="sm" onClick={addQuickLink}><Plus className="w-4 h-4 mr-1" /> Add</Button>
          </div>
          <div className="space-y-4">
            {(formData.quick_links || []).map((link, i) => (
              <div key={i} className="p-4 bg-surface-muted rounded-lg space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium text-gray-600">Link {i + 1}</span>
                  <button type="button" onClick={() => removeQuickLink(i)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Input label="Icon" value={link.icon || ''} onChange={(e) => updateQuickLink(i, 'icon', e.target.value)} />
                  <Input label="Title" value={link.title || ''} onChange={(e) => updateQuickLink(i, 'title', e.target.value)} />
                  <Input label="Description" value={link.description || ''} onChange={(e) => updateQuickLink(i, 'description', e.target.value)} />
                  <Input label="URL (e.g. /vendor/dashboard)" value={link.href || ''} onChange={(e) => updateQuickLink(i, 'href', e.target.value)} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <Button type="submit" disabled={saveMutation.isPending}>
          <Save className="w-4 h-4 mr-2" />
          Save Vendor Support Settings
        </Button>
      </form>
    </div>
  );
};

export default VendorSupportSettings;
